import { TFunction } from 'i18next';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import { FieldItem } from '@lib/interfaces/form';
import { GetLoadOptions, SelectOption } from '@lib/components/Select/types';
import {
  getWidgetSelectorField,
  getActiveInterventionsFields,
  getAnnouncementFields,
  getClockFields,
  getExternalLinkFields,
  getQuickCreationFields,
  getSectorsDeadlineTrackerFields,
  getSitesMapFields,
  getSitesDeadlineTrackerFields,
  getWeatherTodayFields,
  getWeatherWeekFields,
} from './fieldBuilders';

/**
 * Function type for checking user access by component
 */
type GetUserAccessByComponent = (
  component: AccessComponentEnum,
) => AccessLevelEnum;

/**
 * Original button settings from an existing widget
 */
interface OriginalButtonSettings {
  enabled?: boolean | null;
  updatedAt?: string | null;
}

/**
 * Original buttons configuration from an existing widget
 */
interface OriginalButtons {
  task?: OriginalButtonSettings | null;
  intervention?: OriginalButtonSettings | null;
  formTemplate?: OriginalButtonSettings | null;
  asset?: OriginalButtonSettings | null;
  site?: OriginalButtonSettings | null;
  teamMember?: OriginalButtonSettings | null;
}

interface GetFormFieldsArgs {
  t: TFunction<'translation', undefined>;
  getWidgetTemplatesLoadOptions: GetLoadOptions;
  isEditMode?: boolean;
  onWidgetChange?: (widgetOption: SelectOption | null) => void;
  /** Function to check user access by component */
  getUserAccessByComponent?: GetUserAccessByComponent;
  /** Function to check company-level access (plan restrictions) */
  getUserAccessByCompany?: GetUserAccessByComponent;
  /** Original buttons configuration from the widget being edited (for QuickCreation) */
  originalButtons?: OriginalButtons;
}

/**
 * Main function that combines all widget fields.
 *
 * Each widget type has its own field builder in the fieldBuilders/ directory:
 * - getWidgetSelectorField - Main widget selector dropdown
 * - getActiveInterventionsFields - Active interventions widget
 * - getAnnouncementFields - Announcement widget with schedule
 * - getClockFields - Clock widget with timezone
 * - getExternalLinkFields - External link widget
 * - getQuickCreationFields - Quick creation widget with toggles
 * - getSectorsDeadlineTrackerFields - Sectors deadline tracker
 * - getSitesMapFields - Sites map widget
 * - getSitesDeadlineTrackerFields - Sites deadline tracker
 * - getWeatherTodayFields - Today's weather widget
 * - getWeatherWeekFields - Week weather widget
 */
export default function getWidgetFormFields({
  t,
  getWidgetTemplatesLoadOptions,
  isEditMode,
  onWidgetChange,
  getUserAccessByComponent,
  getUserAccessByCompany,
  originalButtons,
}: GetFormFieldsArgs): FieldItem[] {
  return [
    ...getWidgetSelectorField(
      t,
      getWidgetTemplatesLoadOptions,
      isEditMode,
      onWidgetChange,
    ),
    ...getActiveInterventionsFields(t),
    ...getAnnouncementFields(t),
    ...getClockFields(t),
    ...getExternalLinkFields(t),
    ...getQuickCreationFields(t, {
      getUserAccessByComponent,
      getUserAccessByCompany,
      isEditMode,
      originalButtons,
    }),
    ...getSectorsDeadlineTrackerFields(t),
    ...getSitesMapFields(t),
    ...getSitesDeadlineTrackerFields(t),
    ...getWeatherTodayFields(t),
    ...getWeatherWeekFields(t),
  ];
}
